import { formatMinutes, monthLabel, monthOf, periodLabel, summariseDays } from '@kronos/domain'
import type { DayLine, LocalDate, LocalMonth, PayPeriod, PeriodSummary } from '@kronos/domain'
import { Badge, Section, StatTile } from '../primitives'

/**
 * The pieces around the hours calendar: the totals above it, the key under it,
 * and the earlier pay periods as buttons that turn the calendar to them.
 */

const shortPeriod = (p: PayPeriod): string => periodLabel(p).replace(/ \d{4}$/, '')
const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`

/** Each half of the month, then the month — every number a sum of the squares below. */
export function MonthTiles({
  month,
  periods,
  lines,
  today,
}: {
  month: LocalMonth
  periods: readonly [PayPeriod, PayPeriod]
  lines: readonly DayLine[]
  today: LocalDate
}): JSX.Element {
  const whole = summariseDays(lines, periods[0].from, periods[1].to)
  return (
    <div className="stat-tiles hours-tiles">
      {periods.map((p) => {
        const s = summariseDays(lines, p.from, p.to)
        const current = today >= p.from && today <= p.to
        return (
          <div key={p.from} className={`hours-tile${current ? ' is-current' : ''}`}>
            <StatTile label={shortPeriod(p)} value={formatMinutes(s.minutes)} sub={current ? 'This pay period' : p.from > today ? 'Not started' : days(s.daysWorked)} />
          </div>
        )
      })}
      <div className="hours-tile hours-tile--month">
        <StatTile label={monthLabel(month)} value={formatMinutes(whole.minutes)} sub={`${days(whole.daysWorked)} worked`} />
      </div>
      {whole.needsALook > 0 ? (
        <div className="hours-tile">
          <StatTile label="To fix" value={whole.needsALook} sub="Tap the amber days" warning />
        </div>
      ) : null}
    </div>
  )
}

export function HoursLegend(): JSX.Element {
  return (
    <div className="cal-legend" aria-hidden="true">
      <span className="cal-legend__item">
        <span className="cal-swatch cal-swatch--worked" /> Worked — the bar fills at 8h
      </span>
      <span className="cal-legend__item">
        <span className="cal-swatch cal-swatch--look" /> Needs fixing
      </span>
      <span className="cal-legend__item">
        <span className="cal-swatch cal-swatch--line" /> Pay periods split
      </span>
    </div>
  )
}

/** Every earlier pay period, newest first — a press turns the calendar to it. */
export function EarlierPeriods({
  history,
  month,
  onPick,
}: {
  history: readonly PeriodSummary[]
  month: LocalMonth
  onPick: (period: PayPeriod) => void
}): JSX.Element | null {
  if (history.length === 0) return null
  return (
    <Section label="Earlier pay periods">
      <div className="hours-history">
        {history.map((h) => (
          <button key={h.period.from} className="hours-period" aria-pressed={monthOf(h.period.from) === month} onClick={() => onPick(h.period)}>
            <span className="hours-period__main">
              <span className="hours-period__label">{periodLabel(h.period)}</span>
              <span className="hours-period__meta">
                {days(h.days)} · {h.shifts} {h.shifts === 1 ? 'shift' : 'shifts'}
              </span>
            </span>
            <span className="hours-period__side">
              {h.unfixed > 0 ? <Badge tone="warning">{h.unfixed} to fix</Badge> : null}
              <span className="figure">{formatMinutes(h.minutes)}</span>
            </span>
          </button>
        ))}
      </div>
    </Section>
  )
}
